import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import MetaTags from '../../components/MetaTags';
import Loading from '../../components/Loading';
import FooterMenu from '../../components/Footer/FooterMenu';
import Container from '../../components/Layout/Container';
import { Heading2 } from '../../components/Heading';
import { callBackbonePostApi } from '../../services/backboneApiService';

import EventBadge from './EventBadge';
import RsvpForm from './RsvpForm';
import { EVENT_ACTIONS, EVENT_COLORS, EVENT_COPY, EVENTS_ROUTE } from './constants';
import {
  downloadTextFile,
  extractErrorMessage,
  formatCapacitySummary,
  formatEventWhen,
  getEventAvailability,
  isSoftFailure,
} from './helpers';
import { buildIcs, buildIcsFileName } from './ics';

const EventDetail = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadEvent = useCallback(async () => {
    try {
      const response = await callBackbonePostApi({ action: EVENT_ACTIONS.GET_EVENT, eventId }, '', {
        skipAuth: true,
      });
      if (isSoftFailure(response) || !response?.data?.event) {
        setLoadError(extractErrorMessage(response));
        setEvent(null);
      } else {
        setEvent(response.data.event);
        setLoadError('');
      }
    } catch (error) {
      setLoadError(extractErrorMessage(error) || EVENT_COPY.DETAIL_LOAD_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    setIsLoading(true);
    loadEvent();
  }, [loadEvent]);

  const handleAddToCalendar = () => {
    downloadTextFile(
      buildIcsFileName(event),
      buildIcs(event, { url: window.location.href }),
      'text/calendar;charset=utf-8'
    );
  };

  const availability = event ? getEventAvailability(event) : null;

  return (
    <Box sx={{ minHeight: '100vh', background: '#0A0B0C' }}>
      <MetaTags
        title={event ? `${event.title} - Black Box VR` : EVENT_COPY.PAGE_TITLE}
        description={event?.description || EVENT_COPY.PAGE_DESCRIPTION}
      />
      <Container>
        <Box sx={{ pt: { xs: 4, md: 6 }, pb: 8, maxWidth: 760, mx: 'auto' }}>
          <Button
            component={RouterLink}
            to={EVENTS_ROUTE}
            sx={{ textTransform: 'none', mb: 2, px: 0 }}
          >
            {`< ${EVENT_COPY.BACK_TO_EVENTS}`}
          </Button>

          {isLoading && <Loading />}

          {!isLoading && loadError && <Alert severity="error">{loadError}</Alert>}

          {!isLoading && event && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <EventBadge availability={availability} />
              </Stack>
              <Heading2 sx={{ mb: 2, wordBreak: 'break-word' }}>{event.title}</Heading2>
              <Typography fontSize={18} fontWeight={700}>
                {formatEventWhen(event.startsAtMs, event.endsAtMs)}
              </Typography>
              {event.location && (
                <Typography sx={{ color: EVENT_COLORS.muted, mt: 0.5 }}>
                  {event.location}
                </Typography>
              )}
              <Typography sx={{ color: EVENT_COLORS.muted, mt: 1 }} data-testid="capacity-summary">
                {formatCapacitySummary(event)}
              </Typography>
              {event.description && (
                <Typography sx={{ mt: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {event.description}
                </Typography>
              )}

              <Button
                variant="outlined"
                onClick={handleAddToCalendar}
                sx={{ mt: 3, textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
              >
                {EVENT_COPY.ADD_TO_CALENDAR}
              </Button>

              <RsvpForm event={event} availability={availability} onEventChanged={loadEvent} />
            </>
          )}
        </Box>
      </Container>
      <FooterMenu />
    </Box>
  );
};

export default EventDetail;
